import { truncateMiddle } from "./truncate-middle.ts";

export interface TruncateListToFitOptions {
  separator?: string;
  ellipsis?: string;
  extraWidthPerVisibleItem?: number;
}

export interface TruncateListToFitResult {
  text: string;
  isTruncated: boolean;
  visibleCount: number;
  hiddenCount: number;
  visibleItems: readonly string[];
  suffixText: string;
}

export const WIDTH_BUFFER = 0.5 as const;

/**
 * Goal: build the most informative single-line representation that fits `containerWidth`.
 *
 * Behaviour (high level):
 * - If the full list fits, show it.
 * - Otherwise, try showing as many full items as possible plus a `+N` suffix.
 * - If there is still spare room, use it to show a partial next item (end-truncated) before the suffix.
 * - If nothing beyond the first item can fit, fall back to a single (middle-truncated) item plus `+N`.
 *
 * `visibleItems` are the display labels for the rendered items. In the "partial next item" case,
 * the last entry is a truncated label (not the original raw item).
 */
export function truncateListToFit(
  items: readonly string[],
  containerWidth: number,
  measure: (candidate: string) => number,
  options: TruncateListToFitOptions = {},
): TruncateListToFitResult {
  const separator = options.separator ?? ", ";
  const ellipsis = options.ellipsis ?? "…";
  const extraWidthPerVisibleItem = options.extraWidthPerVisibleItem ?? 0;

  const formatSuffix = (hiddenCount: number) => (hiddenCount > 0 ? ` +${hiddenCount}` : "");

  const fullText = items.join(separator);

  if (items.length === 0 || containerWidth <= 0) {
    return {
      text: fullText,
      isTruncated: false,
      visibleCount: items.length,
      hiddenCount: 0,
      visibleItems: items,
      suffixText: "",
    };
  }

  // Use the measurer + a small buffer to avoid pixel rounding causing a 1px clip.
  const fits = (candidate: string, visibleCount: number) => {
    const measured = measure(candidate) + visibleCount * extraWidthPerVisibleItem;
    if (!Number.isFinite(measured)) return true;
    return measured - containerWidth <= WIDTH_BUFFER;
  };

  if (fits(fullText, items.length)) {
    return {
      text: fullText,
      isTruncated: false,
      visibleCount: items.length,
      hiddenCount: 0,
      visibleItems: items,
      suffixText: "",
    };
  }

  // First, find the best "K full items + suffix" that fits.
  let bestFull: TruncateListToFitResult | undefined;

  for (let visibleCount = items.length - 1; visibleCount >= 1; visibleCount--) {
    const hiddenCount = items.length - visibleCount;
    const suffixText = formatSuffix(hiddenCount);
    const visibleItems = items.slice(0, visibleCount);
    const candidate = visibleItems.join(separator) + suffixText;

    if (fits(candidate, visibleCount)) {
      bestFull = { text: candidate, isTruncated: true, visibleCount, hiddenCount, visibleItems, suffixText };
      break;
    }
  }

  if (bestFull) {
    // If we have a fitting full-items prefix, try to use any remaining space to show a partial next item
    // before the suffix (e.g. "item1, item2, next… +N").
    for (let fullCount = Math.min(bestFull.visibleCount, items.length - 1); fullCount >= 1; fullCount--) {
      const nextItem = items[fullCount] ?? "";
      if (!nextItem) continue;

      // The suffix count is based on how many original items remain after the partial item.
      const hiddenCount = items.length - (fullCount + 1);
      const suffixText = formatSuffix(hiddenCount);

      const prefixItems = items.slice(0, fullCount);
      const prefixText = prefixItems.join(separator) + separator;

      // Binary search for the longest end-truncated next-item label that still fits.
      let minLength = 0;
      let maxLength = nextItem.length;

      while (minLength < maxLength) {
        const midLength = Math.ceil((minLength + maxLength) / 2);
        const truncated = truncateEnd(nextItem, midLength, ellipsis);
        const candidate = prefixText + truncated + suffixText;

        if (fits(candidate, fullCount + 1)) {
          minLength = midLength;
        } else {
          maxLength = midLength - 1;
        }
      }

      // Avoid rendering a partial item that is effectively just an ellipsis.
      if (minLength <= ellipsis.length) continue;

      let truncated = truncateEnd(nextItem, minLength, ellipsis);
      let candidate = prefixText + truncated + suffixText;

      // If rounding still clips, drop one more character.
      if (!fits(candidate, fullCount + 1) && minLength > 0) {
        truncated = truncateEnd(nextItem, minLength - 1, ellipsis);
        candidate = prefixText + truncated + suffixText;
      }

      if (!fits(candidate, fullCount + 1)) continue;
      if (truncated === ellipsis || truncated.length === 0) continue;

      // `visibleItems` contains display labels; the last entry is the truncated next-item label.
      const visibleItems = [...prefixItems, truncated];
      const visibleCount = fullCount + 1;

      return { text: candidate, isTruncated: true, visibleCount, hiddenCount, visibleItems, suffixText };
    }

    // If a partial next item doesn't add value, use the best full-items prefix we found.
    return bestFull;
  }

  // No "2+ items + suffix" fits. Fall back to the first item, middle-truncated if needed,
  // and keep the `+N` suffix visible whenever possible.
  const firstItem = items[0] ?? "";
  const hiddenCount = Math.max(0, items.length - 1);
  const suffixText = formatSuffix(hiddenCount);

  let minLength = 0;
  let maxLength = firstItem.length;

  while (minLength < maxLength) {
    const midLength = Math.ceil((minLength + maxLength) / 2);
    const truncated = truncateMiddle(firstItem, midLength, ellipsis) + suffixText;

    if (fits(truncated, 1)) {
      minLength = midLength;
    } else {
      maxLength = midLength - 1;
    }
  }

  let best = truncateMiddle(firstItem, minLength, ellipsis);
  if (!best) {
    best = truncateMiddle(firstItem, 1, ellipsis) || ellipsis || firstItem;
  }

  let candidate = best + suffixText;
  if (!fits(candidate, 1) && minLength > 0) {
    best = truncateMiddle(firstItem, minLength - 1, ellipsis);
    candidate = best + suffixText;
  }

  // If even the smallest "item + suffix" can't fit, return the smallest meaningful fallback we can.
  if (!fits(candidate, 1)) {
    const condensedSuffix = suffixText.trimStart();
    if (condensedSuffix && fits(condensedSuffix, 0)) {
      return {
        text: condensedSuffix,
        isTruncated: true,
        visibleCount: 0,
        hiddenCount: items.length,
        visibleItems: [],
        suffixText: condensedSuffix,
      };
    }

    if (fits(ellipsis, 0)) {
      return {
        text: ellipsis,
        isTruncated: true,
        visibleCount: 0,
        hiddenCount: items.length,
        visibleItems: [],
        suffixText: "",
      };
    }
  }

  return { text: candidate, isTruncated: true, visibleCount: 1, hiddenCount, visibleItems: [best], suffixText };
}

const truncateEnd = (text: string, maxLength: number, ellipsis = "…"): string => {
  if (text.length <= maxLength) return text;

  const ellipsisLength = ellipsis.length;
  const availableLength = maxLength - ellipsisLength;

  if (availableLength <= 0) return ellipsis.slice(0, maxLength);
  return text.slice(0, availableLength) + ellipsis;
};
